// fork(oem): the welcome state's starter categories, rebuilt for the OEM+
// service assistant.
//
// The foundation shipped five generic categories (Learn · Code · Write · Life
// admin · Just chatting) with prompt-engineering boilerplate in each prefill.
// This replaces them with five things a service organisation actually asks on a
// Tuesday morning, drawn from the demo's own suggestion sets — `SUGG` and
// `PSUGG` at source lines 4092–4094, ported in `shared/seed/suggestions.ts`.
//
// Every prompt here is one the local engine answers with real figures. Machine
// ids are interpolated from the data pack's `IDS` map rather than typed, so
// they follow the pack if the story machines are ever renumbered.
//
// Data Schema layer — pure data.
//
// @see src/versions/v1/shared/seed/suggestions.ts — the ported demo prompts
// @see src/versions/v1/shared/assistant/local-answer.ts — what answers them

import {
  Coins,
  FileText,
  Package,
  Siren,
  Truck,
  type LucideIcon,
} from 'lucide-react';
import { IDS } from '../data/core';

export type StarterPrompt = {
  /** Shown in the expanded panel + dropdown row. */
  label: string;
  /**
   * Text written into the composer on click. For the OEM+ assistant the two
   * are the same string: these are complete questions, not prompt scaffolds,
   * so there is nothing to expand on the way in.
   */
  prefill: string;
};

export type StarterCategory = {
  /** Stable id used for the active-expansion state. */
  id: string;
  /** Chip + dropdown-submenu icon. */
  icon: LucideIcon;
  /** Chip label + dropdown-submenu trigger label. */
  label: string;
  /** 4-5 prompts per category. */
  prompts: StarterPrompt[];
};

/** A complete question is its own label. */
const ask = (question: string): StarterPrompt => ({ label: question, prefill: question });

export const STARTER_CATEGORIES: StarterCategory[] = [
  {
    id: 'triage',
    icon: Siren,
    label: 'Triage',
    prompts: [
      ask('What needs a decision today?'),
      ask('What does doing nothing cost us today?'),
      ask('Which jobs are waiting on a person?'),
      ask('Which machines are most likely to fail next and why?'),
      ask(`Who should the ${IDS.T22} brake alert go to?`),
    ],
  },
  {
    id: 'machines',
    icon: Truck,
    label: 'Machines',
    prompts: [
      ask(`Give me the full history of ${IDS.L14}`),
      ask('What is the breakdown trend across the six mines?'),
      ask(`How confident should I be in the ${IDS.J07} prediction?`),
      ask('Which loaders break down most?'),
    ],
  },
  {
    id: 'cost-energy',
    icon: Coins,
    label: 'Cost & energy',
    prompts: [
      ask('Compare cost per operating hour by mine and explain the difference'),
      ask('Break down cost by crew at Kalbarri'),
      ask('Compare emissions by drive type'),
      ask("Why is Kalbarri's cost per operating hour so high?"),
    ],
  },
  {
    id: 'parts-stock',
    icon: Package,
    label: 'Parts & stock',
    prompts: [
      ask(`Do we have brake pads for ${IDS.T22} on site?`),
      ask('Which parts do we need in the next 30 days that are not on the shelf?'),
      ask('Which parts are below minimum at the Broken Ridge store?'),
      ask('Do we have a rotary head motor for S-09 in store?'),
    ],
  },
  {
    id: 'reports',
    icon: FileText,
    label: 'Reports',
    prompts: [
      ask('Generate the weekly status report for Load & Haul'),
      ask('Write the machine risk email for Mount Isolde'),
      ask('Create the Broken Ridge equipment summary presentation'),
      ask("Generate today's S.O.S report"),
    ],
  },
];
